import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { theme } from 'antd';
import { RootState } from 'store/configureStore';
import { TitleListEN, TitleListsCompliance } from 'store/ducks/globalTypes';
import { TitleKeyCompliance } from 'store/ducks/Management/types';
import { TITLE_KEYWORD_MAP } from 'constants/titlKeyword';
import auditFindingsWorkflow from 'constants/auditFindingsWorkflow';
import TitleBar from 'components/TitleBar';
import GoBackButton from 'components/GoBackButton';
import CardComponent from 'components/CardComponent';
import complianceEstAreas from 'constants/complianceEstAreas';
import riskAndControlAreas from 'constants/risksAndControlsAreas';
import testingAreas from 'constants/testingAreas';
import inventoryAreas from 'constants/inventoryAreas';
import riskAndControlAllocationAreas from 'constants/riskAndControlAllocationAreas';
import i18n from 'constants/translation/i18n';
import { useTranslation } from 'react-i18next';
import * as Styled from 'components/Layouts/Layout.styled';
import { TFunction } from 'i18next';

const { useToken } = theme;

const RISK_CONTROL_TYPE_MAP: Record<string, (t?: any) => any[]> = {
  testings: testingAreas,
  inventories: inventoryAreas,
  allocation: riskAndControlAllocationAreas
};

const MainLayout: React.FC = () => {
  const { token } = useToken();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selected } = useParams();

  const lang = useSelector((state: RootState) => state.global.lang);
  const [entriesToShow, setEntriesToShow] = useState<any[]>([]);
  const location = useLocation();

  const ROUTE_TO_TITLE_MAP: Record<string, string> = {
    'rules-to-evidence': 'Rules-to-Evidence Workflow',
    'audit-findings': 'Audit findings',
    'internal-controls': 'Business Process & Risk Control Hub'
  };
  const selectedArea = selected ? ROUTE_TO_TITLE_MAP[selected] || '' : '';

  const { scheme } = useSelector((state: RootState) => state.global);
  const pathname = location.pathname;
  const riskControlSegment = pathname.startsWith('/risk-controls/')
    ? pathname.split('/risk-controls/')[1]
    : undefined;

  const findTitleKeyFromMap = (translatedTitle: string): string | null => {
    const lowerTitle = translatedTitle.toLowerCase();

    // Direct keyword match
    if (TITLE_KEYWORD_MAP[lowerTitle]) {
      return TITLE_KEYWORD_MAP[lowerTitle];
    }

    // Partial keyword match
    for (const [keyword, titleKey] of Object.entries(TITLE_KEYWORD_MAP)) {
      if (lowerTitle.includes(keyword.toLowerCase())) {
        return titleKey;
      }
    }

    return null;
  };

  useEffect(() => {
    if (!selectedArea && !pathname) return;

    i18n.changeLanguage(lang);

    let entries: any[] = [];

    if (pathname === '/internal_controls') {
      entries = riskAndControlAreas(i18n.t);
      setEntriesToShow(entries);
      return;
    }

    const titleKey = findTitleKeyFromMap(selectedArea);
    if (riskControlSegment && RISK_CONTROL_TYPE_MAP[riskControlSegment]) {
      entries = RISK_CONTROL_TYPE_MAP[riskControlSegment](i18n.t);
    } else if (titleKey === 'AUDIT_FINDINGS') {
      entries = auditFindingsWorkflow(i18n.t);
    } else if (titleKey === 'COMP_EST') {
      entries = complianceEstAreas(i18n.t);
    } else if (titleKey === 'INTERNAL_CONTROLS') {
      entries = riskAndControlAreas(i18n.t);
    } else if (titleKey === 'DEPENDENCY_TREE') {
      entries = dependencyTreeAreas(i18n.t);
    }

    setEntriesToShow(entries);
  }, [lang, pathname, riskControlSegment]);

  useEffect(() => {
    if (!selectedArea) return;

    const titleKey = findTitleKeyFromMap(selectedArea);

    if (!titleKey) return;

    const redirects: Record<string, string> = {
      LIBRARY: '/library',
      USER_ACCESS: '/user_access',
      WHO_AM_I: '/who_am_i',
      DEPENDENCY_TREE: '/dependencies'
    };

    const target = redirects[titleKey];

    if (target && location.pathname !== target) {
      navigate(target);
    }
  }, [selectedArea, navigate, location.pathname]);

  const getTitleKeyFromTitle = (
    selectedTitle: string,
    titleEnum: Record<TitleKeyCompliance, string>
  ): TitleKeyCompliance => {
    return (
      (Object.keys(titleEnum) as TitleKeyCompliance[]).find(
        (key) => titleEnum[key] === selectedTitle
      ) ?? 'COMP_EST'
    );
  };

  const TitleKeyComplianceLayout = getTitleKeyFromTitle(
    selectedArea,
    TitleListsCompliance['en']
  );

  const titles = TitleListsCompliance[lang] || TitleListEN;

  const getPageTitle = () => {
    if (pathname === '/internal_controls') {
      return (
        i18n.t('INTERNAL_CONTROLS') || 'Business Process & Risk Control Hub'
      );
    }
    if (riskControlSegment === 'testings') {
      return i18n.t('TESTING');
    }
    if (riskControlSegment === 'reportings') {
      return i18n.t('REPORTING');
    }
    if (riskControlSegment === 'allocation') {
      return i18n.t('ALLOCATION_MAIN');
    }
    if (riskControlSegment === 'inventories') {
      return i18n.t('INVENTORIES');
    }
    if (selectedArea === 'Audit findings') {
      return i18n.t('AUDIT_FINDING');
    }

    return titles[TitleKeyComplianceLayout];
  };

  if (selectedArea === 'Library') return null;

  return (
    <Styled.StyledLayout token={token}>
      <TitleBar
        title={getPageTitle()}
        goBackButtons={
          pathname === '/internal_controls' ? (
            <GoBackButton to='/working_area' text={t('GO_TO_WORKING_AREAS')} />
          ) : riskControlSegment ? (
            <>
              <GoBackButton to='/internal_controls' text={t('GO_BACK')} />
              <GoBackButton
                to='/working_area'
                text={t('GO_TO_WORKING_AREAS')}
              />
            </>
          ) : selectedArea === 'Audit findings' ? (
            <>
              <GoBackButton
                to='/workingAreaDetails/rules-to-evidence'
                text={t('GO_BACK')}
              />
              <GoBackButton
                to='/working_area'
                text={t('GO_TO_WORKING_AREAS')}
              />
            </>
          ) : (
            <GoBackButton to='/working_area' text={t('GO_TO_WORKING_AREAS')} />
          )
        }
      />
      <Styled.HorizontalLayout theme={scheme}>
        <Styled.Container>
          <Styled.GridContainer token={token}>
            {entriesToShow.map((item: any) => (
              <Styled.RelativeContainer key={item.area}>
                <CardComponent
                  area={item.title}
                  infoText={item.infoText}
                  href={item.href}
                  textOnTop=''
                />
              </Styled.RelativeContainer>
            ))}
          </Styled.GridContainer>
        </Styled.Container>
      </Styled.HorizontalLayout>
    </Styled.StyledLayout>
  );
};

export default MainLayout;
function dependencyTreeAreas(
  t: TFunction<['translation', ...string[]], undefined>
): any[] {
  throw new Error('Function not implemented.');
}
